// client/src/components/pages/Settings/LexiStarAssets/AssetDrawer.jsx
//
// Right-hand panel for one asset: details, registration aliases (the names
// tickets and providers know it by; T9-S02 resolves registrations through them)
// and ownership history (D26). Non-admins only get their own organisation's
// ownership periods from the API.

import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaTrash, FaTag, FaHistory, FaExclamationTriangle } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import { formatAppDate, formatAppDateTime } from '../../../../utils/dateUtils';
import { getAsset, addAlias, removeAlias } from '../../../../services/lexistarAssets';
import { ASSET_TYPE_LABELS, ASSET_STATUS_LABELS, hasRegistrationChars } from './AssetForm';

const ALIAS_KIND_LABELS = {
  registration: 'Registration',
  fleet_no: 'Fleet no',
  external_vehicle_id: 'External vehicle id',
};

const inputClasses =
  'w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm';

const EMPTY_ALIAS = { kind: 'registration', value: '', source_system: '' };

const Detail = ({ label, value, mono }) => (
  <div>
    <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</dt>
    <dd className={`mt-0.5 text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>
      {value === null || value === undefined || value === '' ? (
        <span className="text-slate-400 italic">—</span>
      ) : (
        value
      )}
    </dd>
  </div>
);

const AssetDrawer = ({ assetId, isOpen, canManage, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aliasForm, setAliasForm] = useState(EMPTY_ALIAS);
  const [aliasError, setAliasError] = useState(null);
  const [savingAlias, setSavingAlias] = useState(false);

  const load = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getAsset(assetId));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load asset');
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    if (isOpen) {
      setData(null);
      setAliasForm(EMPTY_ALIAS);
      setAliasError(null);
      load();
    }
  }, [isOpen, load]);

  if (!isOpen) return null;

  const asset = data?.asset;
  const manageable = canManage && asset && ['Transporter', 'Admin'].includes(asset.access_type);

  const handleAddAlias = async (e) => {
    e.preventDefault();
    if (!hasRegistrationChars(aliasForm.value)) {
      setAliasError('Enter a value with letters or digits');
      return;
    }
    setSavingAlias(true);
    setAliasError(null);
    try {
      await addAlias(assetId, {
        kind: aliasForm.kind,
        value: aliasForm.value.trim(),
        source_system: aliasForm.source_system.trim() || null,
      });
      setAliasForm(EMPTY_ALIAS);
      await load();
      onChanged?.();
    } catch (err) {
      setAliasError(err.response?.data?.error || err.message || 'Failed to add alias');
    } finally {
      setSavingAlias(false);
    }
  };

  const handleRemoveAlias = async (alias) => {
    setAliasError(null);
    try {
      await removeAlias(assetId, alias.id);
      await load();
    } catch (err) {
      setAliasError(err.response?.data?.error || err.message || 'Failed to remove alias');
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={asset ? `Asset ${asset.registration}` : 'Asset'}
        className="h-full w-full max-w-lg bg-surface shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 font-mono truncate">
              {asset ? asset.registration : 'Asset'}
            </h3>
            {asset && (
              <p className="text-sm text-slate-500 mt-0.5">
                {ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}
                {asset.fleet_no ? ` · Fleet no ${asset.fleet_no}` : ''} · Owned by{' '}
                {asset.tenant_name || `organisation ${asset.tenant_id}`}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 transition-colors mt-1"
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {loading && !data && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-slate-200 border-t-primary-500"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2 text-sm">
              <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {asset && (
            <>
              <section>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <Detail
                    label="Status"
                    value={ASSET_STATUS_LABELS[asset.status] || asset.status}
                  />
                  <Detail label="Class" value={asset.vehicle_class} />
                  <Detail
                    label="Make / model"
                    value={[asset.make, asset.model].filter(Boolean).join(' ')}
                  />
                  <Detail label="Year" value={asset.year} />
                  <Detail label="VIN" value={asset.vin} mono />
                  <Detail label="Axles" value={asset.axle_config} />
                  <Detail
                    label="Tare"
                    value={asset.tare_kg ? `${asset.tare_kg.toLocaleString()} kg` : null}
                  />
                  <Detail
                    label="GVM / GCM"
                    value={[asset.gvm_kg, asset.gcm_kg]
                      .filter(Boolean)
                      .map((n) => `${n.toLocaleString()} kg`)
                      .join(' / ')}
                  />
                  <Detail
                    label="Tank"
                    value={asset.tank_capacity_l ? `${asset.tank_capacity_l} L` : null}
                  />
                  <Detail label="Added" value={formatAppDate(asset.created_at)} />
                </dl>
              </section>

              <section>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-1">
                  <FaTag className="text-slate-400" size={12} /> Aliases
                </h4>
                <p className="text-xs text-slate-500 mb-3">
                  Other names this asset is known by on tickets and provider feeds.
                </p>
                {aliasError && <p className="mb-2 text-sm text-red-600">{aliasError}</p>}
                {data.aliases.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No aliases.</p>
                ) : (
                  <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                    {data.aliases.map((a) => (
                      <li key={a.id} className="px-3 py-2 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 mr-2">
                            {ALIAS_KIND_LABELS[a.kind] || a.kind}
                          </span>
                          <span className="text-sm font-mono text-slate-900">{a.value}</span>
                          {a.source_system && (
                            <span className="text-xs text-slate-500"> · {a.source_system}</span>
                          )}
                        </div>
                        {manageable && (
                          <button
                            onClick={() => handleRemoveAlias(a)}
                            aria-label={`Remove alias ${a.value}`}
                            className="text-slate-400 hover:text-red-600 transition-colors"
                          >
                            <FaTrash size={12} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {manageable && (
                  <form onSubmit={handleAddAlias} className="mt-3 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2">
                      <ThemedSelect
                        value={aliasForm.kind}
                        onChange={(e) => setAliasForm((f) => ({ ...f, kind: e.target.value }))}
                        className={inputClasses}
                        aria-label="Alias kind"
                      >
                        {Object.entries(ALIAS_KIND_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </ThemedSelect>
                      <input
                        type="text"
                        value={aliasForm.value}
                        onChange={(e) => setAliasForm((f) => ({ ...f, value: e.target.value }))}
                        placeholder="Value, e.g. JX12KLGP"
                        aria-label="Alias value"
                        maxLength={64}
                        className={`${inputClasses} font-mono`}
                      />
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={aliasForm.source_system}
                        onChange={(e) =>
                          setAliasForm((f) => ({ ...f, source_system: e.target.value }))
                        }
                        placeholder="Source system (optional)"
                        aria-label="Alias source"
                        maxLength={60}
                        className={inputClasses}
                      />
                      <button
                        type="submit"
                        disabled={savingAlias}
                        className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400 transition-all whitespace-nowrap flex-shrink-0"
                      >
                        Add alias
                      </button>
                    </div>
                  </form>
                )}
              </section>

              <section>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-1">
                  <FaHistory className="text-slate-400" size={12} /> Ownership history
                </h4>
                <p className="text-xs text-slate-500 mb-3">
                  Periods when other organisations owned this asset are shown to global admins only.
                </p>
                <ol className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {data.ownership.map((o) => (
                    <li
                      key={o.asset_ownership_id}
                      className="px-3 py-2 flex items-center justify-between gap-3"
                    >
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {o.tenant_name || `Organisation ${o.tenant_id}`}
                          {o.tenant_code && (
                            <span className="text-slate-500 font-normal"> ({o.tenant_code})</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 tabular-nums">
                          {formatAppDateTime(o.effective_from)} →{' '}
                          {o.effective_to ? formatAppDateTime(o.effective_to) : 'now'}
                        </div>
                      </div>
                      {o.is_current && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                          Current
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default AssetDrawer;
